// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Group, Stack, Text } from '@mantine/core';
import { type TriageSortState } from '@polarissleep/shared';
import { IconAlertSquareRoundedFilled } from '@tabler/icons-react';
import type { DataTableSortStatus } from 'mantine-datatable';
import { DataTable } from 'mantine-datatable';
import type { JSX } from 'react';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { type TestingQueueEntry, useTestingQueue } from '../../hooks/useTestingQueue';
import { sortStatusToTriageSortState, triageSortStateToSortStatus } from './useTriageQueueUrlState';

function getAhiSeverity(ahi: number): string {
  if (ahi < 5) {
    return 'Low';
  }
  if (ahi < 15) {
    return 'Mild';
  }
  if (ahi < 30) {
    return 'Moderate';
  }
  return 'Severe';
}

function formatMins(secs: number): string {
  return `${Math.round((secs / 60) * 10) / 10} mins`;
}

function formatHMS(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function isInLabReferralRow(entry: TestingQueueEntry): boolean {
  return entry.rowVariant === 'inLabReferral';
}

function getMissingMetricMarker(entry: TestingQueueEntry): string {
  return '—';
}

interface TestingTableProps {
  page: number;
  pageSize: number;
  search: string;
  onPageChange: (page: number) => void;
  sortState: TriageSortState | null;
  onSortChange: (next: TriageSortState | null) => void;
  hideTestPatients: boolean;
  onTotalChange?: (total: number) => void;
}

export function TestingTable({
  page,
  pageSize,
  search,
  onPageChange,
  sortState,
  onSortChange,
  hideTestPatients,
  onTotalChange,
}: TestingTableProps): JSX.Element {
  const navigate = useNavigate();
  const { entries, loading, total } = useTestingQueue({
    page,
    pageSize,
    search,
    sort: sortState ?? undefined,
    hideTestPatients,
  });

  useEffect(() => onTotalChange?.(total), [onTotalChange, total]);

  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  const columns = useMemo(
    () => [
      {
        accessor: 'patientName' as const,
        title: 'Patient',
        sortable: true,
        render: (entry: TestingQueueEntry) => (
          <Stack gap={0}>
            <Anchor
              size="sm"
              fw="bold"
              underline="always"
              onClick={async () => {
                await navigate(`/Patient/${entry.patientId}`);
              }}
              style={{ cursor: 'pointer' }}
            >
              {entry.patientName}
            </Anchor>
            {entry.orderingProviderName && (
              <Text size="xs" c="dimmed">
                Order Placed by {entry.orderingProviderName}
              </Text>
            )}
            {isInLabReferralRow(entry) && (
              <Text size="xs" c="dimmed">
                In-lab PSG · monitoring adherence
              </Text>
            )}
          </Stack>
        ),
      },
      {
        accessor: 'totalSleepTime' as const,
        sortable: true,
        title: (
          <>
            Total
            <br />
            Sleep Time
          </>
        ),
        render: (entry: TestingQueueEntry) => {
          if (isInLabReferralRow(entry)) {
            return <Text size="sm">{getMissingMetricMarker(entry)}</Text>;
          }
          const tst = entry.obs['TotalSleepTime'];
          return <Text size="sm">{tst !== undefined ? formatHMS(tst) : getMissingMetricMarker(entry)}</Text>;
        },
      },
      {
        accessor: 'validSleepTime' as const,
        sortable: true,
        title: (
          <>
            Technically
            <br />
            Valid Sleep Time
          </>
        ),
        render: (entry: TestingQueueEntry) => {
          if (isInLabReferralRow(entry)) {
            return <Text size="sm">{getMissingMetricMarker(entry)}</Text>;
          }
          const tvst = entry.obs['TotalAH_CentralValidSleepTime'];
          return <Text size="sm">{tvst !== undefined ? formatHMS(tvst) : getMissingMetricMarker(entry)}</Text>;
        },
      },
      {
        accessor: 'ahi' as const,
        sortable: true,
        title: 'pAHI (3%)',
        render: (entry: TestingQueueEntry) => {
          const ahi = entry.obs['AHI'];
          if (ahi === undefined) {
            return <Text size="sm">{getMissingMetricMarker(entry)}</Text>;
          }
          return (
            <Group gap={4} wrap="nowrap">
              <Text size="sm">{ahi}</Text>
              <Text size="sm" c="dimmed">
                {getAhiSeverity(ahi)}
              </Text>
            </Group>
          );
        },
      },
      {
        accessor: 'ahiCentral' as const,
        sortable: true,
        title: 'pAHIc (3%)',
        render: (entry: TestingQueueEntry) => {
          if (isInLabReferralRow(entry)) {
            return <Text size="sm">{getMissingMetricMarker(entry)}</Text>;
          }
          const ahi = entry.obs['AHI'];
          const ahiCentral = entry.obs['AHICentral'];
          if (ahiCentral === undefined) {
            return <Text size="sm">{getMissingMetricMarker(entry)}</Text>;
          }
          const centralPct = ahi !== undefined && ahi > 0 ? Math.round((ahiCentral / ahi) * 100) : undefined;
          return (
            <Group gap={4} wrap="nowrap">
              <Text size="sm">{ahiCentral}</Text>
              {centralPct !== undefined && (
                <Text size="sm" c="dimmed">
                  ({centralPct}%)
                </Text>
              )}
              {centralPct !== undefined && centralPct > 50 && (
                <IconAlertSquareRoundedFilled size={15} color="#FA5252" style={{ flexShrink: 0 }} />
              )}
            </Group>
          );
        },
      },
      {
        accessor: 'heartRate' as const,
        title: (
          <>
            Heart Rate
            <br />
            (Mean, Min, Max)
          </>
        ),
        render: (entry: TestingQueueEntry) => {
          if (isInLabReferralRow(entry)) {
            return <Text size="sm">{getMissingMetricMarker(entry)}</Text>;
          }
          const meanPR = entry.obs['MeanPRSleep'];
          const minPR = entry.obs['MinPRSleep'];
          const maxPR = entry.obs['MaxPRSleep'];
          if (meanPR === undefined || minPR === undefined || maxPR === undefined) {
            return <Text size="sm">{getMissingMetricMarker(entry)}</Text>;
          }
          return (
            <Group gap={4} wrap="nowrap">
              <Text size="sm">{`${meanPR} / ${minPR} / ${maxPR}`}</Text>
              {maxPR > 100 && <IconAlertSquareRoundedFilled size={15} color="#FA5252" style={{ flexShrink: 0 }} />}
            </Group>
          );
        },
      },
      {
        accessor: 'supineAhi' as const,
        title: (
          <>
            Supine/Non-
            <br />
            Supine AHI
          </>
        ),
        render: (entry: TestingQueueEntry) => {
          if (isInLabReferralRow(entry)) {
            return <Text size="sm">{getMissingMetricMarker(entry)}</Text>;
          }
          const supineAhi = entry.obs['Supine_AHI'];
          const nonSupineAhi = entry.obs['NonSupine_AHI'];
          if (supineAhi === undefined || nonSupineAhi === undefined) {
            return <Text size="sm">{getMissingMetricMarker(entry)}</Text>;
          }
          return (
            <Group gap={4} wrap="nowrap">
              <Text size="sm">{supineAhi}</Text>
              <Text size="sm" c="dimmed">
                / {nonSupineAhi}
              </Text>
            </Group>
          );
        },
      },
      {
        accessor: 'satBelow88' as const,
        sortable: true,
        title: (
          <>
            Oxygen Saturation
            <br />
            {'<'}88
          </>
        ),
        render: (entry: TestingQueueEntry) => {
          if (isInLabReferralRow(entry)) {
            return <Text size="sm">{getMissingMetricMarker(entry)}</Text>;
          }
          const satBelow88 = entry.obs['SatBelowEqual88'];
          if (satBelow88 === undefined) {
            return <Text size="sm">{getMissingMetricMarker(entry)}</Text>;
          }
          return <Text size="sm">{formatMins(satBelow88)}</Text>;
        },
      },
      {
        accessor: 'meanNadir' as const,
        sortable: true,
        title: (
          <>
            Mean O2
            <br />
            Nadirs
          </>
        ),
        render: (entry: TestingQueueEntry) => {
          if (isInLabReferralRow(entry)) {
            return <Text size="sm">{getMissingMetricMarker(entry)}</Text>;
          }
          const meanNadir = entry.obs['MeanNadirDesaturations'];
          if (meanNadir === undefined) {
            return <Text size="sm">{getMissingMetricMarker(entry)}</Text>;
          }
          return (
            <Group gap={4} wrap="nowrap">
              <Text size="sm">{meanNadir}%</Text>
              {meanNadir < 90 && <IconAlertSquareRoundedFilled size={15} color="#FA5252" style={{ flexShrink: 0 }} />}
            </Group>
          );
        },
      },
      {
        accessor: 'actions' as const,
        title: 'Actions',
        render: (entry: TestingQueueEntry) => {
          if (isInLabReferralRow(entry)) {
            return (
              <Group gap="xs" wrap="nowrap">
                <Anchor
                  size="sm"
                  fw="bold"
                  underline="always"
                  onClick={async () => {
                    await navigate(`/Patient/${entry.patientId}/diagnostic-reports`);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  View Results
                </Anchor>
                <Anchor
                  size="sm"
                  fw="bold"
                  underline="always"
                  onClick={async () => {
                    await navigate(`/Patient/${entry.patientId}/Appointment/schedule`);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  Schedule Appointment
                </Anchor>
              </Group>
            );
          }
          const hasFinalReport = entry.reportStatus === 'final';
          return hasFinalReport ? (
            <Group gap="xs" wrap="nowrap">
              <Anchor
                size="sm"
                fw="bold"
                underline="always"
                onClick={async () => {
                  await navigate(`/Patient/${entry.patientId}/diagnostic-reports`);
                }}
                style={{ cursor: 'pointer' }}
              >
                View Results
              </Anchor>
              <Anchor
                size="sm"
                fw="bold"
                underline="always"
                onClick={async () => {
                  await navigate(`/Patient/${entry.patientId}/Appointment/schedule`);
                }}
                style={{ cursor: 'pointer' }}
              >
                Schedule Appointment
              </Anchor>
            </Group>
          ) : (
            <Group gap="xs" wrap="nowrap">
              <Anchor
                size="sm"
                fw="bold"
                underline="always"
                onClick={async () => {
                  await navigate(`/Patient/${entry.patientId}/devices`);
                }}
                style={{ cursor: 'pointer' }}
              >
                Check Device
              </Anchor>
              <Anchor
                size="sm"
                fw="bold"
                underline="always"
                onClick={async () => {
                  await navigate(`/Patient/${entry.patientId}/Appointment/schedule`);
                }}
                style={{ cursor: 'pointer' }}
              >
                Schedule Appointment
              </Anchor>
            </Group>
          );
        },
      },
    ],
    [navigate]
  );

  // Bridge: map the 'name' sort key ↔ 'patientName' accessor for mantine-datatable.
  const sortStatus: DataTableSortStatus<TestingQueueEntry> = (() => {
    const status = triageSortStateToSortStatus(sortState);
    if (!status) {
      return { columnAccessor: '', direction: 'desc' as const };
    }
    const columnAccessor = status.columnAccessor === 'name' ? 'patientName' : status.columnAccessor;
    return { columnAccessor, direction: status.direction };
  })();

  return (
    <>
      {total > 0 && (
        <Text size="sm" c="dimmed">
          {start} - {end} of {total}
        </Text>
      )}
      <DataTable
        striped
        highlightOnHover
        fetching={loading && entries.length === 0}
        records={entries}
        columns={columns}
        idAccessor="patientId"
        noRecordsText="No patients in testing"
        page={page}
        recordsPerPage={pageSize}
        totalRecords={total}
        onPageChange={onPageChange}
        paginationSize="sm"
        minHeight={200}
        sortStatus={sortStatus}
        onSortStatusChange={(status: DataTableSortStatus<TestingQueueEntry>) => {
          // Translate 'patientName' accessor back to the 'name' sort key.
          const normalizedAccessor = status.columnAccessor === 'patientName' ? 'name' : status.columnAccessor;
          const next = sortStatusToTriageSortState({ columnAccessor: normalizedAccessor, direction: status.direction });
          // 3-state cycle: unsorted → desc → asc → unsorted. mantine-datatable
          // only toggles desc ↔ asc; we intercept the asc → desc transition on
          // the same column (the user's third click) and clear the sort.
          if (
            next &&
            sortState &&
            sortState.column === next.column &&
            sortState.direction === 'asc' &&
            next.direction === 'desc'
          ) {
            onSortChange(null);
          } else {
            onSortChange(next);
          }
        }}
      />
    </>
  );
}
